import { CSSObject, SerializedStyles } from '@emotion/react'
import * as RadixTooltip from '@radix-ui/react-tooltip'
import { motion } from 'framer-motion'
import React, { useCallback, useEffect, useState } from 'react'
import { Theme, ZIndex } from '@opallabs/gem'
import { InverseThemeProvider, ThemeProvider, useTheme } from '@opallabs/hooks'
import * as styles from './styles'

export type TooltipProps = React.ComponentProps<typeof RadixTooltip.Content> & {
  content: React.ReactElement | string
  contentStyles?: SerializedStyles | CSSObject
  /** Radix default is 700ms */
  delay?: number
  disabled?: boolean
  disableHoverableContent?: boolean
  /**
   * Opens the tooltip when it turns `true`, whatever the pointer and focus do. Escape or a click elsewhere still closes
   * it until `forceOpen` turns `true` again; other close requests, such as a scroll, don’t. For a trigger that can’t
   * take focus itself, such as content in a table cell, where the cell takes focus.
   */
  forceOpen?: boolean
  onOpenChange?: (open: boolean) => void
  showArrow?: boolean
  /** @deprecated Should only be used by components that cannot be wrapped in theme provider */
  legacyTheme?: Theme
  /** @default ZIndex.Tooltip */
  zIndex?: number
}

/**
 * @note To make `<Tooltip>` work with another React component, the other component must
 * * use `forwardRef` and pass the `forwardRef` prop to some element
 * * take rest parameters and pass them to the same element
 *
 * Minimal example:
 * ```typescript
 * const Dingus = forwardRef<HTMLSpanElement, DingusProps>(({ xyz, ...otherProps }, ref) => <span ref={ref} {...otherProps}>test</span>)
 * ```
 */
export const Tooltip: React.FC<TooltipProps> = ({
  content,
  contentStyles,
  delay,
  disabled,
  disableHoverableContent,
  forceOpen = false,
  onOpenChange,
  showArrow,
  legacyTheme,
  children,
  zIndex = ZIndex.Tooltip,
  onEscapeKeyDown,
  onPointerDownOutside,
  ...contentProps
}) => {
  const theme = useTheme()
  const [open, setOpen] = useState(false)
  const [isForceOpenDismissed, setIsForceOpenDismissed] = useState(false)

  const handleOpenChange = useCallback(
    (open: boolean) => {
      open = open && !disabled
      setOpen(open)
      onOpenChange?.(open)
    },
    [disabled, onOpenChange]
  )

  const dismissForceOpen = (event: Event) => {
    if (forceOpen && !event.defaultPrevented) setIsForceOpenDismissed(true)
  }

  useEffect(() => {
    if (!forceOpen) setIsForceOpenDismissed(false)
  }, [forceOpen])

  useEffect(() => {
    if (disabled && open) {
      handleOpenChange(false)
    }
  }, [disabled, open, handleOpenChange])

  if (!React.isValidElement(children)) {
    throw new Error(
      'A valid react element must be passed as a child to the Tooltip'
    )
  }

  return (
    <RadixTooltip.Provider>
      <RadixTooltip.Root
        delayDuration={disabled ? 0 : delay}
        disableHoverableContent={disableHoverableContent}
        open={(forceOpen && !disabled && !isForceOpenDismissed) || open}
        onOpenChange={handleOpenChange}
      >
        <RadixTooltip.Trigger asChild>{children}</RadixTooltip.Trigger>
        <Portal>
          <RadixTooltip.Content
            {...contentProps}
            onEscapeKeyDown={(event) => {
              onEscapeKeyDown?.(event)
              dismissForceOpen(event)
            }}
            onPointerDownOutside={(event) => {
              onPointerDownOutside?.(event)
              dismissForceOpen(event)
            }}
            className="lapidary-tooltip-content"
            style={{ zIndex }}
          >
            <motion.div
              css={[styles.content(legacyTheme ?? theme), contentStyles]}
              animate={{ scale: 1, opacity: 1 }}
              initial={{ scale: 0.75, opacity: 0 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            >
              {showArrow && (
                <RadixTooltip.Arrow css={styles.arrow(legacyTheme ?? theme)} />
              )}
              <InverseThemeProvider theme={legacyTheme}>
                {content}
              </InverseThemeProvider>
            </motion.div>
          </RadixTooltip.Content>
        </Portal>
      </RadixTooltip.Root>
    </RadixTooltip.Provider>
  )
}
Tooltip.displayName = 'Lapidary: Tooltip'

const Portal: React.FC<React.ComponentProps<typeof RadixTooltip.Portal>> = ({
  children,
  ...props
}) => {
  const theme = useTheme()

  /**
   * RadixTooltip.Portal mounts its children outside of the DOM hierarchy, typically
   * directly under the <body> element. Because this will move the components
   * outside of the scope of any CSS variables applied in the existing DOM tree,
   * we need to reprovide Gem's <ThemeProvider> with the current theme to rescope them.
   */
  return (
    <RadixTooltip.Portal {...props}>
      <ThemeProvider
        theme={theme}
        // Portalled components should have absolute/fixed position
        // Reset the size properties, so that the portal itself
        // does not affect the container's layout
        css={{ blockSize: 'auto', inlineSize: 'auto' }}
      >
        {children}
      </ThemeProvider>
    </RadixTooltip.Portal>
  )
}
Portal.displayName = 'Lapidary: Portal ← Tooltip'
